import { useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useHireModal } from "@/components/HireModal/HireModalContext";
import { EMPLOYEE_AVATARS, EMPLOYEE_HUE } from "@/components/VirtualEmployeesSection/employees";
import { SOLUTION_PREFIX } from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";
import { SLIDE_SOLUTION } from "./slides";

/** Cuando aparece el primer mensaje, y cada cuanto el siguiente (segundos). */
const FIRST_AT_S = 0.5;
const STEP_S = 1.6;
/** Cuanto "escribe" el empleado antes de que aparezca su respuesta. */
const TYPING_S = 0.9;

type Chat = { from: string; text: string }[];

interface EmployeesPanelProps {
  /** Cuantas veces se activo este slide: arranca en 1 y decide que empleado se muestra. */
  run: number;
  /** La persona toco algo: el slide deja de avanzar solo. */
  onInteract: () => void;
}

/**
 * Slide 1: una demo en vivo de un empleado virtual -conversacion corta- con el
 * selector de los cinco y el boton que abre el modal de dos datos.
 *
 * Cada vez que el slide vuelve a activarse muestra al empleado siguiente (asi,
 * en una segunda vuelta, se ve otro), salvo que la persona haya elegido uno.
 * `run` tambien es la key de la conversacion: al remontarse, las burbujas
 * vuelven a armarse desde cero.
 *
 * Las cinco conversaciones estan en el diccionario, pero solo se renderiza la
 * del empleado mostrado: el texto de los cinco puestos queda en la seccion de
 * empleados, que esta justo debajo en la misma pagina.
 */
function EmployeesPanel({ run, onInteract }: EmployeesPanelProps) {
  const { t, locale } = useLocale();
  const hire = useHireModal();
  const demo = t.hero.slide;
  const members = t.virtualTeam.members;

  const [chosen, setChosen] = useState<number | null>(null);
  const shown = chosen ?? Math.max(0, run - 1) % members.length;
  const member = members[shown];
  const Avatar = EMPLOYEE_AVATARS[member.id];
  const chat = (demo.chats as Record<string, Chat>)[member.id] ?? [];

  return (
    <div
      className="hs-employees"
      style={{ "--emp-hue": EMPLOYEE_HUE[member.id] } as CSSProperties}
    >
      <header className="hs-employees__bar hs-enter" style={{ "--i": 0 } as CSSProperties}>
        <span className="hs-employees__avatar" aria-hidden="true">
          {Avatar && <Avatar />}
        </span>
        <span className="hs-employees__who">
          <strong>{member.role}</strong>
          <small>{demo.example}</small>
        </span>
      </header>

      <ol className="hs-employees__chat" key={`${shown}-${run}`}>
        {chat.map((message, n) => {
          const isOut = message.from === "out";
          const revealAt = FIRST_AT_S + n * STEP_S;
          // La respuesta del empleado arranca "escribiendo" un rato antes.
          const appearAt = isOut ? Math.max(0, revealAt - TYPING_S) : revealAt;

          return (
            <li
              key={message.text}
              className={`hs-bubble hs-bubble--${message.from}`}
              style={
                {
                  "--at": `${(appearAt + 0.45).toFixed(2)}s`,
                  "--reveal": `${(revealAt + 0.45).toFixed(2)}s`,
                } as CSSProperties
              }
            >
              {isOut && (
                <span className="hs-bubble__dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
              )}
              <span className="hs-bubble__text">{message.text}</span>
            </li>
          );
        })}
      </ol>

      <div className="hs-employees__chips hs-enter" style={{ "--i": 2 } as CSSProperties}>
        <span className="sr-only">{demo.chooseLabel}</span>
        {members.map((m, index) => {
          const ChipAvatar = EMPLOYEE_AVATARS[m.id];

          return (
            <button
              key={m.id}
              type="button"
              className="hs-employees__chip"
              style={{ "--emp-hue": EMPLOYEE_HUE[m.id] } as CSSProperties}
              aria-label={m.role}
              title={m.role}
              aria-pressed={index === shown}
              onClick={() => {
                onInteract();
                setChosen(index);
              }}
            >
              {ChipAvatar && <ChipAvatar />}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="hs-employees__cta hs-enter"
        style={{ "--i": 3 } as CSSProperties}
        onClick={() => hire.open(member.id)}
      >
        <span>{demo.cta}</span>
        <span className="hs-employees__cta-role">{member.role}</span>
      </button>

      <Link
        to={`${SOLUTION_PREFIX[locale]}/${SLIDE_SOLUTION.employees}`}
        className="hs-more hs-enter"
        style={{ "--i": 4 } as CSSProperties}
      >
        {t.hero.stage.viewSolution} →
      </Link>
    </div>
  );
}

export default EmployeesPanel;
